import { Controller } from "@hotwired/stimulus"

// ============================================================
// BuyConfirmController — 슬롯 구매 확인 + 완료 모달 통합 처리
// ============================================================
// 구매 플로우:
//   1. open()       → 구매 확인 모달 표시 (PIN 입력)
//   2. submit()     → fetch PATCH /transfers/:id/buy (JSON)
//   3. 성공 시      → 확인 모달 닫고 완료 모달 표시 (TX hash + 스캔 링크)
//   4. confirm()    → 완료 모달 닫고 페이지 새로고침
//
// Targets:
//   confirmModal   — 구매 확인 모달 전체
//   completeModal  — 구매 완료 모달 전체
//   titleEl        — 확인 모달 내 매물 제목
//   pinInput       — PIN 입력 필드
//   submitBtn      — 구매 실행 버튼
//   errorMsg       — 확인 모달 내 에러 메시지 영역
//   txHashEl       — 완료 모달 TX hash 텍스트
//   scanLinkEl     — 완료 모달 스캔사이트 링크
// ============================================================
export default class extends Controller {
  static targets = [
    "confirmModal", "completeModal",
    "titleEl", "pinInput", "submitBtn", "errorMsg",
    "txHashEl", "scanLinkEl",
    "loadingPanel", "loadingDots"
  ]

  #buyUrl      = null
  #scanBase    = null
  #escHandler  = null
  #dotsTimer   = null

  connect() {
    this.#escHandler = (e) => { if (e.key === "Escape") this.#closeAll() }
    document.addEventListener("keydown", this.#escHandler)
  }

  disconnect() {
    document.removeEventListener("keydown", this.#escHandler)
    document.body.style.overflow = ""
  }

  open(event) {
    event.preventDefault()
    const btn = event.currentTarget

    this.#buyUrl   = btn.dataset.buyUrl
    this.#scanBase = btn.dataset.scanBase || ""

    if (this.hasTitleElTarget)  this.titleElTarget.textContent  = btn.dataset.listingTitle || ""
    if (this.hasPinInputTarget) this.pinInputTarget.value       = ""
    if (this.hasErrorMsgTarget) { this.errorMsgTarget.textContent = ""; this.errorMsgTarget.classList.add("hidden") }

    this.confirmModalTarget.classList.remove("hidden")
    document.body.style.overflow = "hidden"
    if (this.hasPinInputTarget) this.pinInputTarget.focus()
  }

  async submit(event) {
    event.preventDefault()
    if (!this.#buyUrl) return

    const pin = this.hasPinInputTarget ? this.pinInputTarget.value.trim() : ""
    if (!pin) {
      this.#showError(this.submitBtnTarget?.dataset.pinRequired || "PIN을 입력해주세요.")
      return
    }

    this.#setLoading(true)
    this.#clearError()

    let succeeded = false
    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
      const resp = await fetch(this.#buyUrl, {
        method:  "PATCH",
        headers: {
          "Content-Type":  "application/json",
          "X-CSRF-Token":  csrfToken || "",
          "Accept":        "application/json",
        },
        body: JSON.stringify({ pin }),
      })

      const data = await resp.json()

      if (data.success) {
        succeeded = true
        this.#setLoading(false)
        this.#showCompleteModal(data.tx_hash, data.scan_url)
      } else {
        this.#showError(data.error || "구매 처리 중 오류가 발생했습니다.")
      }
    } catch (err) {
      this.#showError("네트워크 오류가 발생했습니다. 다시 시도해주세요.")
    } finally {
      if (!succeeded) this.#setLoading(false)
    }
  }

  close() {
    this.confirmModalTarget.classList.add("hidden")
    document.body.style.overflow = ""
    if (this.hasPinInputTarget) this.pinInputTarget.value = ""
    this.#clearError()
  }

  closeOnOverlay(event) {
    if (event.target === event.currentTarget) this.close()
  }

  confirm() {
    this.completeModalTarget.classList.add("hidden")
    document.body.style.overflow = ""
    window.location.href = "/transfers"
  }

  completeCloseOnOverlay(event) {
    if (event.target === event.currentTarget) this.confirm()
  }

  // ── private helpers ──────────────────────────────────────

  #showCompleteModal(txHash, scanUrl) {
    this.confirmModalTarget.classList.add("hidden")

    if (this.hasTxHashElTarget)  this.txHashElTarget.textContent = txHash  || ""
    if (this.hasScanLinkElTarget) {
      this.scanLinkElTarget.href        = scanUrl || "#"
      this.scanLinkElTarget.textContent = txHash  || ""
    }

    this.completeModalTarget.classList.remove("hidden")
  }

  #showError(msg) {
    if (!this.hasErrorMsgTarget) return
    this.errorMsgTarget.textContent = msg
    this.errorMsgTarget.classList.remove("hidden")
  }

  #clearError() {
    if (!this.hasErrorMsgTarget) return
    this.errorMsgTarget.textContent = ""
    this.errorMsgTarget.classList.add("hidden")
  }

  #setLoading(on) {
    if (this.hasLoadingPanelTarget) {
      if (on) {
        this.loadingPanelTarget.classList.remove("hidden")
        this.#startDots()
      } else {
        this.loadingPanelTarget.classList.add("hidden")
        this.#stopDots()
      }
    }
    if (this.hasSubmitBtnTarget) {
      this.submitBtnTarget.disabled = on
    }
  }

  #startDots() {
    if (!this.hasLoadingDotsTarget) return
    let count = 0
    this.#dotsTimer = setInterval(() => {
      this.loadingDotsTarget.textContent = ".".repeat((count % 3) + 1)
      count++
    }, 500)
  }

  #stopDots() {
    if (this.#dotsTimer) { clearInterval(this.#dotsTimer); this.#dotsTimer = null }
    if (this.hasLoadingDotsTarget) this.loadingDotsTarget.textContent = ""
  }

  #closeAll() {
    if (!this.confirmModalTarget.classList.contains("hidden")) this.close()
    if (!this.completeModalTarget.classList.contains("hidden")) this.confirm()
  }
}
